import React, { useState, useEffect, useRef } from "react";
import { BiCheckbox } from "react-icons/bi";
import { FaBars, FaTimes, FaArrowLeft, FaArrowRight, FaUsers } from "react-icons/fa";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import axios from "axios";

const BoardMemberDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [boardMembers, setBoardMembers] = useState([]);
  const [subordinates, setSubordinates] = useState([]);
  const headerRef = useRef(null);

  const API_BASE_URL = 'https://backend.dlapakistan.com/api/board';

  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl;
    }
    if (imageUrl.startsWith('/media/')) {
      return `https://backend.dlapakistan.com${imageUrl}`;
    }
    if (imageUrl.startsWith('/')) {
      return `https://backend.dlapakistan.com${imageUrl}`;
    }
    return imageUrl;
  };

  useEffect(() => {
    const fetchMemberData = async () => {
      try {
        setLoading(true);
        
        const membersResponse = await axios.get(`${API_BASE_URL}/members/active/`);
        const members = membersResponse.data;
        setBoardMembers(members);
        
        let foundMember = members.find(m => m.id.toString() === id);
        
        if (!foundMember && id) {
          const memberResponse = await axios.get(`${API_BASE_URL}/members/${id}/`);
          foundMember = memberResponse.data;
        }
        
        if (foundMember) {
          foundMember.image_url = getImageUrl(foundMember.image_url);
          foundMember.subordinates = (foundMember.subordinates || []).map(sub => ({
            ...sub,
            image_url: getImageUrl(sub.image_url)
          }));
          setMember(foundMember);
          setSubordinates(foundMember.subordinates || []);
        }
        
      } catch (error) {
        console.error('Error fetching member:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMemberData();
    
    const updateHeaderHeight = () => {
      const header = document.querySelector('header') || 
                    document.querySelector('.header') ||
                    document.querySelector('#header');
      
      if (header) {
        setHeaderHeight(header.offsetHeight);
      } else {
        if (windowWidth < 640) {
          setHeaderHeight(80);
        } else if (windowWidth < 768) {
          setHeaderHeight(96);
        } else {
          setHeaderHeight(120);
        }
      }
    };

    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      updateHeaderHeight();
      if (window.innerWidth > 1029) {
        setIsSidebarOpen(false);
      }
    };
    
    updateHeaderHeight();
    window.addEventListener("resize", handleResize);
    
    const observer = new ResizeObserver(updateHeaderHeight);
    const headerElement = headerRef.current || 
                         document.querySelector('header');
    if (headerElement) observer.observe(headerElement);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (headerElement) observer.unobserve(headerElement);
    };
  }, [id]);

  const isMobileView = windowWidth <= 1029;

  const [sidebarLinks, setSidebarLinks] = useState([
    { text: "All Board Members", border: "#ccc", showIcon: true, path: "/about/meet-the-board", isHeader: true }
  ]);

  useEffect(() => {
    if (boardMembers.length > 0) {
      const memberLinks = boardMembers.map(memberItem => ({
        text: memberItem.name,
        border: "#ccc",
        showIcon: true,
        path: memberItem.route || `/board-member/${memberItem.id}`,
        isHeader: false
      }));
      
      setSidebarLinks([
        { text: "All Board Members", border: "#ccc", showIcon: true, path: "/about/meet-the-board", isHeader: true },
        ...memberLinks
      ]);
    }
  }, [boardMembers]);

  const handleSidebarClick = (path) => {
    if (path && path !== "#") {
      navigate(path);
      if (isMobileView) {
        setIsSidebarOpen(false);
      }
    }
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const getNextPrevMembers = () => {
    if (!member || boardMembers.length === 0) return { prev: null, next: null };
    
    const currentIndex = boardMembers.findIndex(m => m.id === member.id);
    const prevMember = currentIndex > 0 ? boardMembers[currentIndex - 1] : null;
    const nextMember = currentIndex < boardMembers.length - 1 ? boardMembers[currentIndex + 1] : null;
    
    return { prev: prevMember, next: nextMember };
  };

  const { prev, next } = getNextPrevMembers();

  const getTopSpacing = () => {
    if (windowWidth < 640) return `pt-32`;
    if (windowWidth < 768) return `pt-36`;
    if (windowWidth < 1024) return `pt-40`;
    return `pt-48`;
  };

  const handleTeamMemberClick = (subordinateId, subordinateRoute) => {
    if (subordinateRoute && subordinateRoute.trim() !== "") {
      navigate(subordinateRoute);
    } else {
      navigate(`/board-member/${subordinateId}`);
    }
    window.scrollTo(0, 0);
  };

  const stripHtml = (html) => {
    if (!html) return '';
    const temp = document.createElement('div');
    temp.innerHTML = html;
    return temp.textContent || temp.innerText || '';
  };

  if (loading) {
    return (
      <div 
        className={`w-full ${getTopSpacing()} flex justify-center items-center py-20`}
        ref={headerRef}
      >
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#70B444]"></div>
      </div>
    );
  }

  if (!member) {
    return (
      <div 
        className={`w-full ${getTopSpacing()} text-center py-12`}
        ref={headerRef}
      >
        <h2 className="text-2xl text-gray-600">Member not found</h2>
        <button 
          onClick={() => navigate('/about/meet-the-board')}
          className="mt-4 bg-[#70B444] text-white px-6 py-2 rounded-lg hover:bg-[#5d9a3a] transition-all"
        >
          Back to Board Members
        </button>
      </div>
    );
  }

  const memberImageUrl = member.image_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(member.name)}&background=70B444&color=fff&size=300`;
  const mobileOverlayClass = isMobileView && isSidebarOpen ? "pt-0" : "";
  const hasSubordinates = subordinates.length > 0;

  return (
    <div 
      className={`w-full min-h-screen ${mobileOverlayClass}`}
      ref={headerRef}
    >
      <div className="w-full px-4 mt-52 sm:px-6 md:px-8 lg:px-10 xl:px-12 mx-auto">
        
        <div className="flex flex-wrap items-center mt-10 text-gray-500 mb-4 md:mb-6 text-sm md:text-base">
          <span 
            className="cursor-pointer hover:underline hover:text-[#70B444] transition-colors"
            onClick={() => navigate("/")}
          >
            Home
          </span> 
          <span className="mx-2">»</span>
          <span 
            className="cursor-pointer hover:underline hover:text-[#70B444] transition-colors"
            onClick={() => navigate("/about/meet-the-board")}
          >
            Meet the Board
          </span> 
          <span className="mx-2">»</span>
          <span className="text-[#70B444] font-medium break-words">
            {member.name}
          </span>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 relative pb-8 md:pb-12 max-w-full">
          
          {!isMobileView && (
            <div className="w-full lg:w-72 xl:w-80 shrink-0 bg-[#f7f9fc] p-5 rounded-lg shadow-sm h-fit sticky top-24">
              <div className="bg-[#70B444] text-white p-3 text-center mb-4">
                <h3 className="m-0 text-base lg:text-lg font-semibold">Meet the Board</h3>
              </div>
              {sidebarLinks.map((item, index) => (
                <div
                  key={index}
                  onClick={() => handleSidebarClick(item.path)}
                  className={`flex items-center py-3 text-[#70B444] font-medium cursor-pointer transition-all hover:bg-[rgba(112,180,68,0.1)] px-3 text-sm lg:text-base ${
                    item.border ? "border-b border-gray-200" : ""
                  } ${
                    (location.pathname === item.path || member.route === item.path) && !item.isSeparator && !item.isBackLink
                      ? "bg-[rgba(112,180,68,0.1)] border-l-4 border-l-[#70B444]" 
                      : ""
                  }`}
                >
                  {item.showIcon && <BiCheckbox className="mr-3 text-lg flex-shrink-0" />}
                  <span className="break-words">{item.text}</span>
                </div>
              ))}
            </div>
          )}

          <div className={`flex-1 min-w-0 ${isMobileView ? 'p-0' : 'p-2 md:p-4'} ${
            isMobileView && isSidebarOpen ? "opacity-30 pointer-events-none" : "opacity-100"
          }`}>
            <div className="flex justify-between items-center border-b-2 border-[#70B444] pb-3 md:pb-4 mb-4 md:mb-6 overflow-hidden">
              <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-[#70B444] leading-snug break-words">
                {member.name}
              </h1>
              
              {isMobileView && (
                <button 
                  onClick={toggleSidebar} 
                  className="flex items-center gap-2 text-[#70B444] border border-[#70B444] rounded-lg px-3 py-2 hover:bg-[#70B444] hover:text-white transition-all text-sm font-semibold shrink-0"
                >
                  {isSidebarOpen ? <FaTimes /> : <FaBars />}
                  <span>{isSidebarOpen ? "Close" : "Menu"}</span>
                </button>
              )}
            </div>

            <p className="text-base sm:text-lg md:text-xl text-gray-900 mb-6 break-words">
              {member.position}
            </p>

            {/* Image and Info Grid with Smaller Image */}
            <div className="flex flex-col lg:flex-row gap-6 md:gap-8 mb-8 md:mb-10">
              {/* Smaller Image Section - Reduced width */}
              <div className="w-full lg:w-1/4 xl:w-1/5">
                <div className="bg-gray-100 rounded-lg overflow-hidden">
                  <img 
                    src={memberImageUrl}
                    alt={member.name}
                    className="w-full h-auto object-contain hover:scale-105 transition-transform duration-300"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(member.name)}&background=70B444&color=fff&size=300`;
                    }}
                  />
                </div>
              </div>

              {/* Content Section - Takes more space */}
              <div className="w-full lg:w-3/4 xl:w-4/5">
                <div className="prose prose-lg max-w-full break-words">
                  {member.description ? (
                    <div 
                      className="about-content" 
                      style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                      dangerouslySetInnerHTML={{ __html: member.description }}
                    />
                  ) : (
                    <div className="text-gray-500 text-center py-8">
                      <p>No biography available for this member.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Team Members Section - Original larger card sizes, 4 cards per row */}
            {hasSubordinates && (
              <div className="mt-8 md:mt-10 pt-6 border-t border-gray-200">
                <h3 className="text-xl md:text-2xl font-bold text-[#70B444] mb-6 flex items-center gap-3">
                  <FaUsers className="text-[#70B444]" />
                  Team Members ({subordinates.length})
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-7">
                  {subordinates.map((subordinate) => {
                    const subImageUrl = subordinate.image_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(subordinate.name)}&background=70B444&color=fff&size=400`;
                    const subPlainDescription = stripHtml(subordinate.description || '');
                    
                    return (
                      <div 
                        key={subordinate.id}
                        className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all cursor-pointer group flex flex-col"
                        onClick={() => handleTeamMemberClick(subordinate.id, subordinate.route)}
                      >
                        <div className="w-full bg-gray-100">
                          {subordinate.image_url ? (
                            <img
                              src={subordinate.image_url}
                              alt={subordinate.name}
                              className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-300"
                              onError={(e) => {
                                e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(subordinate.name)}&background=70B444&color=fff&size=400`;
                              }}
                            />
                          ) : (
                            <div className="w-full aspect-square flex items-center justify-center bg-gradient-to-br from-[#70B444] to-[#5d9a3a] text-white text-5xl font-bold">
                              {subordinate.name.charAt(0)}
                            </div>
                          )}
                        </div>
                        <div className="p-5 flex flex-col flex-1">
                          <h4 className="text-xl font-bold text-[#70B444] mb-2 group-hover:text-[#5d9a3a] transition-colors line-clamp-1">
                            {subordinate.name}
                          </h4>
                          <p className="text-gray-700 font-medium text-base mb-3 line-clamp-2 min-h-[3rem]">
                            {subordinate.position}
                          </p>
                          <p className="text-gray-500 text-sm line-clamp-2 mb-4 min-h-[2.5rem]">
                            {subPlainDescription || "No biography available."}
                          </p>
                          <button className="text-[#70B444] font-semibold hover:text-[#5d9a3a] transition-colors text-sm flex items-center gap-1 mt-auto">
                            View Profile →
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="mt-8 md:mt-10 pt-6 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row justify-between gap-4">
                {prev && (
                  <div className="text-left">
                    <span className="text-gray-500 text-sm block mb-1">Previous</span>
                    <button 
                      onClick={() => navigate(prev.route || `/board-member/${prev.id}`)}
                      className="flex items-center gap-2 text-[#70B444] font-medium hover:text-[#5d9a3a] transition-colors text-left group"
                    >
                      <FaArrowLeft className="group-hover:-translate-x-1 transition-transform" />
                      <span className="font-semibold truncate">{prev.name}</span>
                    </button>
                  </div>
                )}
                
                {next && (
                  <div className={`text-right ${!prev ? 'ml-auto' : ''}`}>
                    <span className="text-gray-500 text-sm block mb-1">Next</span>
                    <button 
                      onClick={() => navigate(next.route || `/board-member/${next.id}`)}
                      className="flex items-center gap-2 text-[#70B444] font-medium hover:text-[#5d9a3a] transition-colors group"
                    >
                      <span className="font-semibold truncate">{next.name}</span>
                      <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {isMobileView && (
        <>
          <div 
            className={`fixed top-0 right-0 h-full w-[85vw] max-w-sm bg-white z-[60] shadow-2xl transform transition-transform duration-300 ease-in-out ${
              isSidebarOpen ? "translate-x-0" : "translate-x-full"
            }`}
            style={{ top: `${headerHeight}px`, height: `calc(100vh - ${headerHeight}px)` }}
          >
            <div className="flex flex-col h-full">
              <div className="p-4 border-b flex justify-between items-center bg-[#70B444] text-white">
                <span className="font-bold text-lg">Meet the Board</span>
                <FaTimes 
                  className="cursor-pointer text-2xl" 
                  onClick={toggleSidebar} 
                />
              </div>
              <div className="overflow-y-auto flex-1 p-2">
                {sidebarLinks.map((item, index) => (
                  <div
                    key={index}
                    onClick={() => handleSidebarClick(item.path)}
                    className={`p-3 md:p-4 text-base border-b border-gray-100 flex items-center hover:bg-gray-50 ${
                      (location.pathname === item.path || member.route === item.path)
                        ? "text-[#70B444] font-semibold bg-green-50 border-l-4 border-l-[#70B444]" 
                        : "text-gray-700"
                    }`}
                  >
                    {item.showIcon && <BiCheckbox className="mr-3 text-xl text-[#70B444] flex-shrink-0" />}
                    <span>{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          {isSidebarOpen && (
            <div
              className="fixed inset-0 bg-black/50 z-[50] transition-opacity"
              style={{ top: `${headerHeight}px` }}
              onClick={toggleSidebar}
            />
          )}
        </>
      )}

      <style jsx>{`
        :global(body) {
          overflow-x: hidden;
          width: 100%;
        }

        .about-content {
          overflow-wrap: anywhere;
          word-break: break-word;
          max-width: 100%;
        }

        .about-content, .about-content * {
          background-color: transparent !important;
          max-width: 100% !important;
        }

        .about-content p {
          margin-bottom: 1.2em;
          line-height: 1.8;
          color: #374151;
          word-break: break-word;
        }

        .about-content h1, .about-content h2, .about-content h3 {
          color: #70B444;
          margin-top: 1.5rem;
          margin-bottom: 1rem;
        }

        .about-content h1 { font-size: 2.5rem; }
        .about-content h2 { font-size: 2rem; }
        .about-content h3 { font-size: 1.5rem; }

        .about-content hr {
          border: none;
          border-top: 1px solid #D1D5DB;
          margin: 2rem 0;
        }

        .about-content ul, .about-content ol {
          margin-left: 1.5rem;
          margin-bottom: 1.5rem;
        }

        .about-content li {
          margin-bottom: 0.5rem;
        }

        .about-content a {
          color: #70B444;
          text-decoration: none;
        }

        .about-content a:hover {
          text-decoration: underline;
        }

        .line-clamp-1 {
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default BoardMemberDetail;